// Analytics/services/endpoints/clinical.ts — SKIN-35 / SKIN-39
import axios, { CancelToken } from 'axios';
import { PatientsWithoutImagesRow } from '../../PatientsWithoutImages';
import { OpenVisitsRow } from '../../OpenVisits';
import { DermatologistActivityRow } from '../../ActivityPerDermatologist';
import { CohortSummaryData } from '../../CohortSummaryCard';
import { DiseaseBreakdownCoverage, DiseaseBreakdownRow } from '../../DiseaseBreakdownByIcd11Root';
import { LabConfirmationCoverageData } from '../../LabConfirmationCoverage';
import { InvestigationComparisonRow } from '../../InvestigationComparisonTable';

const ANALYTICS_BASE_URL = import.meta.env.VITE_APP_API_URL + '/analytics';

const getAuthHeaders = () => ({ headers: { Authorization: localStorage.getItem('jwt') } });

// Cancellation goes through axios's own CancelToken, NOT an AbortSignal. The pinned
// axios (0.21.1) predates AbortController: `signal` is absent from its request config
// type and its xhr adapter never reads one (`grep -c signal
// node_modules/axios/lib/adapters/xhr.js` → 0), so a signal passed here would be
// silently dropped and cancel nothing. `cancelToken` is the mechanism this version
// actually honours — the adapter calls `request.abort()` off it. See SKIN-260.
//
// A cancelled request rejects with a `Cancel`, not an error; callers must branch on
// `axios.isCancel` so a supersession does not surface as a failed load.
export const clinicalServices = {
    // GET /analytics/:uuidInvestigation/clinical/visits-without-images
    // The endpoint is gated by FUNCTIONALITY.CLINICAL + MEDICAL_READ server-side
    // and returns { status, data: VisitWithoutImagesRow[] } with opaque ids only.
    getVisitsWithoutImages: async (
        uuidInvestigation: string,
        cancelToken?: CancelToken,
    ): Promise<PatientsWithoutImagesRow[]> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/visits-without-images`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data || [];
        throw response.data;
    },

    // SKIN-695 — GET /analytics/:uuid/clinical/open-visits
    // Gated server-side by WEB_VISIT_STATUS + APPROVE_VISIT; returns DRAFT/legacy-OPEN
    // visits older than 2 days (opaque ids + createdBy + firstSubmissionId).
    getOpenVisits: async (
        uuidInvestigation: string,
        cancelToken?: CancelToken,
    ): Promise<OpenVisitsRow[]> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/open-visits`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data || [];
        throw response.data;
    },

    // SKIN-39 — GET /analytics/:uuid/clinical/dermatologist-activity/startDate/:s/endDate/:e
    // Same server-side gate (CLINICAL + MEDICAL_READ). Returns one row per enrolled
    // researcher with range-bounded counts + range-independent last-active.
    getDermatologistActivity: async (
        uuidInvestigation: string,
        startDate: number,
        endDate: number,
        cancelToken?: CancelToken,
    ): Promise<DermatologistActivityRow[]> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/dermatologist-activity/startDate/${startDate}/endDate/${endDate}`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data || [];
        throw response.data;
    },

    // SKIN-270 — GET /analytics/:uuid/clinical/cohort-summary/startDate/:s/endDate/:e
    getCohortSummary: async (
        uuidInvestigation: string,
        startDate: number,
        endDate: number,
        cancelToken?: CancelToken,
    ): Promise<CohortSummaryData> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/cohort-summary/startDate/${startDate}/endDate/${endDate}`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data;
        throw response.data;
    },

    // SKIN-271 — GET /analytics/:uuid/clinical/disease-breakdown/startDate/:s/endDate/:e
    // SKIN-432 — no field in the path: the three diagnosis fields come back as
    // columns of one response, because they are measured in different units and
    // choosing between them forced one unit on all three.
    // SKIN-432 — ?language=xx names the ICD-11 roots for the viewer. Required
    // rather than optional: the API answers in English when it is absent, so
    // leaving it out is a silent choice of language, not a neutral default.
    getDiseaseBreakdown: async (
        uuidInvestigation: string,
        startDate: number,
        endDate: number,
        language: string,
        cancelToken?: CancelToken,
    ): Promise<{ rows: DiseaseBreakdownRow[]; coverage: DiseaseBreakdownCoverage }> => {
        const query = `?language=${encodeURIComponent(language)}`;
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/disease-breakdown/startDate/${startDate}/endDate/${endDate}${query}`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data;
        throw response.data;
    },

    // SKIN-272 — GET /analytics/:uuid/clinical/lab-confirmation-coverage/startDate/:s/endDate/:e
    getLabConfirmationCoverage: async (
        uuidInvestigation: string,
        startDate: number,
        endDate: number,
        cancelToken?: CancelToken,
    ): Promise<LabConfirmationCoverageData> => {
        const url = `${ANALYTICS_BASE_URL}/${uuidInvestigation}/clinical/lab-confirmation-coverage/startDate/${startDate}/endDate/${endDate}`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data;
        throw response.data;
    },

    // SKIN-273 — GET /analytics/clinical/investigation-comparison/startDate/:s/endDate/:e
    // Cross-investigation; no uuid in path. Server returns only investigations the caller can access.
    getInvestigationComparison: async (
        startDate: number,
        endDate: number,
        cancelToken?: CancelToken,
    ): Promise<InvestigationComparisonRow[]> => {
        const url = `${ANALYTICS_BASE_URL}/clinical/investigation-comparison/startDate/${startDate}/endDate/${endDate}`;
        const response = await axios.get(url, { ...getAuthHeaders(), cancelToken });
        if (response.status === 200) return response.data?.data || [];
        throw response.data;
    },
};
